import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { GitLabConnectForm } from '../components/GitLabConnectForm';
import { ScmProviderIcon } from '../components/ScmProviderIcon';

function parseHost(baseUrl: string): string {
  try {
    return new URL(baseUrl).host;
  } catch {
    return baseUrl;
  }
}

export function SettingsScm(): React.ReactElement {
  usePageTitle('Source Control');
  const { isAdmin, workspace, wp } = useWorkspace();
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const [showGitLabForm, setShowGitLabForm] = useState(false);

  const sourcesQuery = orpc.settings.sources.list.queryOptions({ input: {} });
  const { data, isLoading } = useQuery(sourcesQuery);
  const repos = data?.repos ?? [];
  const githubStatus = data?.githubStatus ?? 'none';
  const gitlabConnections = data?.gitlabConnections ?? [];
  const appSlug = workspace.githubAppSlug ?? 'rudder-ai-app';
  const hasRepos = repos.length > 0;

  const githubDescription =
    githubStatus === 'app' && hasRepos
      ? `${repos.length} repositories accessible via the GitHub App.`
      : githubStatus === 'app'
        ? 'GitHub App configured, but no repositories are selected yet.'
        : githubStatus === 'pat'
          ? `Connected via personal access token (${repos.length} repositories).`
          : 'Not connected.';

  const disconnectMutation = useMutation(
    orpc.settings.scm.removeConnection.mutationOptions({
      onSuccess: () => {
        toast.success('GitLab disconnected');
        void queryClient.invalidateQueries({ queryKey: sourcesQuery.queryKey });
      },
      onError: (err: Error) => toast.error(err.message),
    }),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <LoaderCircle size={24} className="animate-spin" />
      </div>
    );
  }

  return (
    <>
      {!isAdmin && (
        <div className="mb-4 rounded border border-amber-200/50 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Only workspace admins can modify SCM connections.
        </div>
      )}

      <div className="mb-6">
        <h2 className="text-sm font-bold tracking-wider text-muted-foreground uppercase">Source Control</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Connect GitHub and GitLab so the agent can read code, open PRs, and run reviews. Sources link to specific
          repositories on the{' '}
          <a href={wp('/settings/sources')} className="text-brand hover:underline">
            Sources
          </a>{' '}
          page.
        </p>
      </div>

      <section className="mb-4 rounded-lg border border-border bg-card p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <ScmProviderIcon provider="github" size={18} />
            <div>
              <h3 className="text-sm font-semibold text-foreground">GitHub</h3>
              <p className="mt-1 text-sm text-muted-foreground">{githubDescription}</p>
            </div>
          </div>
          {isAdmin && githubStatus !== 'pat' && workspace.githubAppSlug && (
            <a
              href={`https://github.com/apps/${appSlug}/installations/${hasRepos ? 'select_target' : 'new'}?state=${workspace.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center gap-2 rounded border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand hover:text-brand"
            >
              <ExternalLink size={12} />
              {hasRepos ? 'Configure GitHub App' : 'Install GitHub App'}
            </a>
          )}
        </div>
      </section>

      <section className="mb-4 rounded-lg border border-border bg-card p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <ScmProviderIcon provider="gitlab" size={18} />
            <div>
              <h3 className="text-sm font-semibold text-foreground">GitLab</h3>
              {gitlabConnections.length === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  No GitLab connections. Add one to link projects to sources.
                </p>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">
                  {gitlabConnections.length} {gitlabConnections.length === 1 ? 'connection' : 'connections'} configured.
                </p>
              )}
            </div>
          </div>
          {isAdmin && !showGitLabForm && (
            <button
              onClick={() => setShowGitLabForm(true)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded border border-input px-2.5 py-1.5 text-sm hover:bg-accent"
            >
              Connect GitLab
            </button>
          )}
        </div>

        {showGitLabForm && (
          <GitLabConnectForm
            onSuccess={() => {
              setShowGitLabForm(false);
              void queryClient.invalidateQueries({ queryKey: sourcesQuery.queryKey });
            }}
            onCancel={() => setShowGitLabForm(false)}
          />
        )}

        {gitlabConnections.length > 0 && (
          <ul className="mt-2 divide-y divide-border/50" aria-label="GitLab connections">
            {gitlabConnections.map((conn) => (
              <li key={conn.id} className="flex items-center justify-between py-2">
                <div>
                  <span className="text-sm text-foreground">
                    {conn.username}@{parseHost(conn.baseUrl)}
                  </span>
                  <span className="ml-2 text-xs text-muted-foreground">({conn.label})</span>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => disconnectMutation.mutate({ connectionId: conn.id })}
                    className="text-xs text-destructive hover:underline"
                  >
                    Disconnect
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
